import { useState } from "react";
import { AlertOctagon, ShieldCheck } from "lucide-react";
import { Button, Input } from "@wa/ui";
import { useT } from "../../../../i18n/I18nProvider";
import { pullMarketingStop, releaseMarketingStop, type MarketingStop } from "../../../../lib/marketingAutomation";
import { Field, Note, Panel, dt } from "../shared";

/**
 * The hard stop.
 *
 * ITS OWN CARD, AT THE TOP, AND NOT PART OF THE SETTINGS FORM. Saving a form is
 * something somebody does while thinking about intervals and caps; one of the
 * fields on that form must not be the thing that stops every letter leaving the
 * building. It is also not the automation switch: turning the clock off stops
 * batches starting on their own and does nothing to the button on a campaign.
 *
 * THE REASON IS REQUIRED TO STOP AND NOT TO RELEASE. The asymmetry is
 * deliberate. Stopping everything is the act that needs a record - the case it
 * exists for is an instruction from a supervisory authority, and "why did
 * sending stop in August" gets asked months later. Releasing it needs that
 * record to already be there, and asking for a second sentence at that moment
 * is friction in front of the recovery rather than in front of the decision.
 */
export function StopCard({
  stop,
  canManage,
  onChanged,
}: {
  stop: MarketingStop;
  canManage: boolean;
  onChanged: () => void;
}) {
  const t = useT();
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);

  async function pull() {
    setBusy(true);
    try {
      await pullMarketingStop(reason.trim());
      setReason("");
      onChanged();
    } finally {
      setBusy(false);
    }
  }

  async function release() {
    setBusy(true);
    try {
      await releaseMarketingStop();
      onChanged();
    } finally {
      setBusy(false);
    }
  }

  if (stop.stopped) {
    return (
      <Panel
        title={t("marketing.auto.stopOnTitle")}
        description={t("marketing.auto.stopOnIntro")}
        icon={<AlertOctagon className="size-4" />}
      >
        <Note tone="bad">
          <span className="block font-[650]">{t("marketing.auto.stopOnHeadline")}</span>
          {/* The operator's own words, verbatim. This is the record. */}
          <span className="mt-1.5 block">{t("marketing.auto.stopReason", { reason: stop.reason })}</span>
          {stop.at ? <span className="mt-1 block opacity-90">{t("marketing.auto.stopAt", { when: dt(stop.at) })}</span> : null}
        </Note>
        <div className="mt-3.5">
          <Button variant="outline" onClick={release} disabled={!canManage || busy}>
            <ShieldCheck className="size-4" />
            {t("marketing.auto.release")}
          </Button>
        </div>
      </Panel>
    );
  }

  return (
    <Panel
      title={t("marketing.auto.stopTitle")}
      description={t("marketing.auto.stopIntro")}
      icon={<AlertOctagon className="size-4" />}
    >
      <div className="flex flex-wrap items-end gap-3">
        <Field label={t("marketing.auto.stopReasonLabel")} hint={t("marketing.auto.stopReasonHint")} className="min-w-[18rem] flex-1">
          <Input value={reason} onChange={(e) => setReason(e.target.value)} disabled={!canManage || busy} />
        </Field>
        <Button variant="destructive" onClick={pull} disabled={!canManage || busy || reason.trim().length === 0}>
          <AlertOctagon className="size-4" />
          {t("marketing.auto.stopNow")}
        </Button>
      </div>
    </Panel>
  );
}
